import "./appProgressInfo.scss";
import React, { useContext, useRef } from "react";
import { CSSTransition } from "react-transition-group";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import { SurveyContext } from "../../store/survey-context";
import { calculateTotalProgress, calculateProgress } from "../../utils";

export default function AppProgressInfo(props) {
  const context = useContext(SurveyContext);
  const progressInfo = useRef();
  const graph = useRef();
  const headline = useRef();
  const headlineHover = useRef();
  const { t } = useTranslation("common");
  const { customProgress, dataApi } = props;
  const { userProfile, survey } = context;
  const progress = {
    chapterOneProgress: userProfile.percentageCompletionValues?.chapterOne,
    chapterTwoProgress: userProfile.percentageCompletionValues?.chapterTwo,
    chapterThreeProgress: userProfile.percentageCompletionValues?.chapterThree,
    chapterFourProgress: userProfile.percentageCompletionValues?.chapterFour,
    overallProgress: userProfile.percentageCompletionValues?.overall,
  };
  const overallProgress =
    typeof customProgress !== "undefined"
      ? customProgress.overallProgress
      : calculateTotalProgress(survey);

  const progressOne = dataApi
    ? progress.chapterOneProgress
    : calculateProgress(1, survey);
  const progressTwo = dataApi
    ? progress.chapterTwoProgress
    : calculateProgress(2, survey);
  const progressThree = dataApi
    ? progress.chapterThreeProgress
    : calculateProgress(3, survey);
  const progressFour = dataApi
    ? progress.chapterFourProgress
    : calculateProgress(4, survey);

  return (
    <>
      <div className={classnames("app-status", props.type)}>
        <CSSTransition
          nodeRef={progressInfo}
          in={!props.isHover}
          timeout={300}
          classNames="fade"
          unmountOnExit
        >
          <div className="app-status__progress-info" ref={progressInfo}>
            <div className="app-status__status-value">
              {dataApi
                ? progress.overallProgress
                : overallProgress
                ? overallProgress
                : "0"}
              %
            </div>
            <small className="label">{t("progress.complete")}</small>
          </div>
        </CSSTransition>
        <CSSTransition
          nodeRef={graph}
          in={props.type === "header" ? props.isHover : true}
          timeout={300}
          classNames="fade"
          unmountOnExit
        >
          <div className="app-status__graph" ref={graph}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 135.9 136.5">
              <defs>
                <clipPath id="clip-path" transform="translate(-1.05 -5.66)">
                  <path
                    id="mask"
                    className="cls-1"
                    d="M0,0V142.72H136V0ZM77.53,68.28h-24V0h24Z"
                  />
                </clipPath>
              </defs>
              <g>
                <g data-name="Layer 1">
                  <g id="WoodRingsNav">
                    <g id="Rings">
                      <g className="cls-2">
                        <g id="Thin" className="cls-3">
                          <path
                            id="_4"
                            data-name="4"
                            className="cls-4"
                            d="M75.42,14.52c12.16.46,22.58,2.36,28.4,9.91,9.86,12.8,25.37,24.31,27.49,42.15,3.41,28.51-8.17,32.58-19.54,45.45-11,12.44-7.71,22-39.87,25.95-17.55,2.17-38.51,2.26-51.38-7.87s-16.8-24-12.95-48.92c1-6.79-.68-13.12-2.06-19.43-2-9.12-3.29-18.21,5.52-28.61,10.1-11.9,26.23-18.51,44.2-18.75"
                            transform="translate(-1.05 -5.66)"
                          />

                          <path
                            id="_3"
                            data-name="3"
                            className="cls-4"
                            d="M75.42,29.82c7.93.56,14.64,2.26,18.56,7.37,7.34,9.54,18.8,18.17,20.35,31.46,2.5,21.18-6.11,24.29-14.55,33.87C91.59,111.84,93.87,119,70,121.94c-13.15,1.64-28.84,1.68-38.42-5.9S18.94,98,21.8,79.34c.79-5.1-.46-9.84-1.45-14.58-1.44-6.85-2.32-13.66,4.3-21.4,7.12-8.31,18.17-13.07,30.58-13.75"
                            transform="translate(-1.05 -5.66)"
                          />

                          <path
                            id="_2"
                            data-name="2"
                            className="cls-4"
                            d="M75.42,45.36c3.67.67,6.73,2,8.72,4.58C89,56.24,96.38,62,97.35,70.71c1.59,13.85-4,16-9.55,22.3S83.69,103.94,68,105.9C59.28,107,48.87,107,42.58,102S34.16,89.82,36,77.48c.52-3.39-.26-6.56-.86-9.72-.87-4.58-1.34-9.11,3.08-14.18,4.12-4.71,10.13-7.61,17-8.57"
                            transform="translate(-1.05 -5.66)"
                          />

                          <path
                            id="_1"
                            data-name="1"
                            className="cls-4"
                            d="M75.42,60a5.15,5.15,0,0,1,.53.61c2.7,3.6,6.74,6.91,7.25,11.86.81,7.75-2.33,9.08-5.39,12.65s-2.61,6.3-11.39,7.45c-5.09.67-11.1.63-14.64-2.29C48,87.15,46.84,83,47.91,75.94a19.25,19.25,0,0,0-.37-5.69c-.39-2.67-.53-5.32,2.07-8.16a14.08,14.08,0,0,1,5.62-3.68"
                            transform="translate(-1.05 -5.66)"
                          />
                        </g>
                        <g id="Thick_Rings" data-name="Thick Rings">
                          <path
                            id="_4-2"
                            style={{
                              strokeDashoffset: ` ${
                                399 - 399 * (progressFour / 100)
                              } `,
                            }}
                            data-name="4"
                            className="cls-5"
                            d="M75.42,14.52c12.16.46,22.58,2.36,28.4,9.91,9.86,12.8,25.37,24.31,27.49,42.15,3.41,28.51-8.17,32.58-19.54,45.45-11,12.44-7.71,22-39.87,25.95-17.55,2.17-38.51,2.26-51.38-7.87s-16.8-24-12.95-48.92c1-6.79-.68-13.12-2.06-19.43-2-9.12-3.29-18.21,5.52-28.61,10.1-11.9,26.23-18.51,44.2-18.75"
                            transform="translate(-1.05 -5.66)"
                          />

                          <path
                            id="_3-2"
                            style={{
                              strokeDashoffset: ` ${
                                292 - 292 * (progressThree / 100)
                              } `,
                            }}
                            data-name="3"
                            className="cls-5"
                            d="M75.42,29.82c7.93.56,14.64,2.26,18.56,7.37,7.34,9.54,18.8,18.17,20.35,31.46,2.5,21.18-6.11,24.29-14.55,33.87C91.59,111.84,93.87,119,70,121.94c-13.15,1.64-28.84,1.68-38.42-5.9S18.94,98,21.8,79.34c.79-5.1-.46-9.84-1.45-14.58-1.44-6.85-2.32-13.66,4.3-21.4,7.12-8.31,18.17-13.07,30.58-13.75"
                            transform="translate(-1.05 -5.66)"
                          />

                          <path
                            id="_2-2"
                            style={{
                              strokeDashoffset: ` ${
                                185 - 185 * (progressTwo / 100)
                              } `,
                            }}
                            data-name="2"
                            className="cls-5"
                            d="M75.42,45.36c3.67.67,6.73,2,8.72,4.58C89,56.24,96.38,62,97.35,70.71c1.59,13.85-4,16-9.55,22.3S83.69,103.94,68,105.9C59.28,107,48.87,107,42.58,102S34.16,89.82,36,77.48c.52-3.39-.26-6.56-.86-9.72-.87-4.58-1.34-9.11,3.08-14.18,4.12-4.71,10.13-7.61,17-8.57"
                            transform="translate(-1.05 -5.66)"
                          />

                          <path
                            style={{
                              strokeDashoffset: ` ${
                                100 - 100 * (progressOne / 100)
                              } `,
                            }}
                            id="_1-2"
                            data-name="1"
                            className="cls-5"
                            d="M75.42,60a5.15,5.15,0,0,1,.53.61c2.7,3.6,6.74,6.91,7.25,11.86.81,7.75-2.33,9.08-5.39,12.65s-2.61,6.3-11.39,7.45c-5.09.67-11.1.63-14.64-2.29C48,87.15,46.84,83,47.91,75.94a19.25,19.25,0,0,0-.37-5.69c-.39-2.67-.53-5.32,2.07-8.16a14.08,14.08,0,0,1,5.62-3.68"
                            transform="translate(-1.05 -5.66)"
                          />
                        </g>
                      </g>
                    </g>
                    <g id="numbers">
                      <g id="_4-4" data-name="4">
                        <path
                          className="cls-6"
                          d="M61.83,13.93a.24.24,0,0,1-.23-.27c0-.19.07-.29.29-.61L67,5.88a.45.45,0,0,1,.37-.22h.27q.24,0,.24.12a.31.31,0,0,1-.08.18l-5.09,7c-.19.26,0,.26.45.26h2.48c.38,0,.45,0,.45-.42V9.9a.33.33,0,0,1,.14-.32,2,2,0,0,1,.82-.28c.22,0,.22.18.22.5v3.09c0,.3,0,.37.32.37h1a.59.59,0,0,1,0,.67h-.87c-.4,0-.48,0-.48.35V15c0,1.27,0,1.48.69,1.56l.42,0c.09.1.08.36,0,.4-.58,0-1.17,0-1.67,0s-1.3,0-2,0c-.11,0-.13-.3,0-.4l.72,0c.67,0,.72-.32.72-1.56v-.67c0-.38-.05-.4-.47-.4Z"
                          transform="translate(-1.05 -5.66)"
                        />
                      </g>
                      <g id="_3-4" data-name="3">
                        <path
                          className="cls-6"
                          d="M61.94,33.21a.61.61,0,0,1,.62-.62c.39,0,.6.32.77.57a.89.89,0,0,0,.85.48,3.4,3.4,0,0,0,2.87-3.13c0-1.41-.68-2.61-2.07-2.61a2.34,2.34,0,0,0-1.73.78.41.41,0,0,1-.35-.56,13.23,13.23,0,0,0,2.56-1.39,2,2,0,0,0,.85-1.54A1.29,1.29,0,0,0,65,23.82a2.55,2.55,0,0,0-2.16,1.4A.26.26,0,0,1,62.5,25a3.29,3.29,0,0,1,2.9-1.94c1.31,0,2.22.71,2.22,1.84,0,.66-.25,1.28-1.71,2.05-.08.16,0,.23.19.24a2.4,2.4,0,0,1,2.32,2.47,4.28,4.28,0,0,1-1.55,3.17,5.38,5.38,0,0,1-3.34,1.37C62.81,34.2,61.94,34,61.94,33.21Z"
                          transform="translate(-1.05 -5.66)"
                        />
                      </g>
                      <g id="_2-4" data-name="2">
                        <path
                          className="cls-6"
                          d="M66.34,48.67a2.24,2.24,0,0,0,1.51-.36,2.32,2.32,0,0,0,.51-.65.31.31,0,0,1,.35.1,7.35,7.35,0,0,1-.73,2c-.66,0-1.33-.05-2.07-.05H63.59c-.9,0-1.43,0-1.78.05a.24.24,0,0,1-.11-.21s.08-.11.18-.18A13.53,13.53,0,0,0,64.33,47c1-1.14,2.28-2.69,2.28-4.4,0-1.46-.7-2.34-2-2.34a2.36,2.36,0,0,0-2.08,1.6.26.26,0,0,1-.38-.21,3.36,3.36,0,0,1,3.22-2.25A2.48,2.48,0,0,1,68,42c0,1-.47,2.08-1.72,3.53l-1,1.12c-1.31,1.5-1.46,1.76-1.46,1.85s.08.15.5.15Z"
                          transform="translate(-1.05 -5.66)"
                        />
                      </g>
                      <g id="_1-4" data-name="1">
                        <path
                          className="cls-6"
                          d="M64.81,58.62c0-1.09,0-1.44-.77-1.51l-.77-.06a.21.21,0,0,1,0-.35,7.47,7.47,0,0,0,2.65-.93.18.18,0,0,1,.15.08c0,.29-.06,1.09-.06,2v6.21c0,1.23,0,1.5.69,1.55l.67,0c.1.1.08.35,0,.4-.63,0-1.35,0-1.91,0s-1.29,0-1.92,0c-.11,0-.12-.3,0-.4l.66,0c.67,0,.69-.32.69-1.55Z"
                          transform="translate(-1.05 -5.66)"
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </div>
        </CSSTransition>
        <CSSTransition
          nodeRef={headlineHover}
          in={props.isHover}
          timeout={300}
          classNames="fade"
          unmountOnExit
        >
          <div
            className="app-status__headline app-status__headline--hover"
            ref={headlineHover}
          >
            <span>{t("progress.headline")}</span>
          </div>
        </CSSTransition>
        <CSSTransition
          nodeRef={headline}
          in={props.type === "header" ? !props.isHover : false}
          timeout={300}
          classNames="fade"
          unmountOnExit
        >
          <div className="app-status__headline" ref={headline}>
            <span>{t("progress.headline")}</span>
          </div>
        </CSSTransition>
      </div>
    </>
  );
}
